import combinations from 'combinations';
import { cloneDeep } from 'lodash';
import { IntlShape } from 'react-intl';
import { getEvent } from '../commands/sportsCommands';
import { accaSystemBet } from '../data-types/betting/bettingConstants';
import { SYSTEM_BET_TYPE } from '../data-types/betting/bettingEnums';
import {
  BetCombinations,
  OutcomeSelectionByEventChanged,
  PendingBet,
  SystemBetStakeDetails,
  SystemBetTypeExtended,
} from '../data-types/betting/bettingInterfaces';
import { AvailableLanguage } from '../data-types/international/internationalEnums';
import { offerStatuses, sportCategories, sportIdsLegacyToNew } from '../data-types/sports/sportsConstants';
import { Outcome, Ticket, TicketSystemBetDetailsInfoExtended } from '../data-types/sports/sportsInterfaces';
import { PendingBetsCounterData, SubscribeEventInfo } from '../data-types/ticket/ticketInterfaces';
import { createCompoundKey, getOfferTranslatedOfferName } from '../international/translation/offers';
import { trackSportradarAddToBeslip } from '../services/sportradarPixel';
import {
  addPendingBet,
  removePendingBet,
  removeSystemBetStakeDetails,
  removeSystemBetStakeValidationStatus,
  setSystemBetPlacingOrder,
} from '../state/actions/betActions';
import { store } from '../state/store';
import { getSystemBetsGlobalAllEWStatus } from './bet';
import { isEventInSubscriptionQueue } from './event';
import { getFinalPayout } from './taxes/payout-taxes';
import { getEWOddsForEachBetForAccumulatorBet } from './ticket';

// set the offer name and odds amount for betslip cards
export const getOfferState = (
  bet: PendingBet,
  intl?: IntlShape,
): {
  offerName: string;
  odds: number;
  oddsString: string;
  fractionalOdds: string;
  outcomeId: number;
  outcomeName: string;
  offerStatus: number;
  eachWayOdds: string;
  placeOdds: number;
} => {
  const { event, offerId, outcomeId, extraValue } = bet;
  let offerName = '';
  let odds = 0;
  let oddsString = '';
  let fractionalOdds = '';
  let outcomeName = '';
  let eachWayOdds = '1';
  let placeOdds = 1;
  let offerStatus = offerStatuses.ENABLED;
  const offer =
    event.isRacing && event.raceOffers
      ? event.raceOffers
      : event.details && event.details.offers.filter(offerItem => offerItem.id === offerId);
  if (offer && offer.length === 1) {
    const outcome = offer[0].outcomes.filter(
      outcomeItem => outcomeItem.outcomeId === outcomeId && (!extraValue || extraValue === outcomeItem.extraValue),
    );
    if (outcome && outcome.length === 1) {
      const translatedOfferName = intl
        ? getOfferTranslatedOfferName(
            createCompoundKey(sportIdsLegacyToNew[event.sportId], offer[0].sourceOfferTypeId, offer[0].sourceOfferTypeInputSourceId),
            offer[0].name,
            intl,
          )
        : offer[0].name;
      offerName = `${translatedOfferName}: ${outcome[0].outcomeName}`;
      outcomeName = outcome[0].outcomeName;
      oddsString = outcome[0].oddsStr;
      odds = outcome[0].odds;
      fractionalOdds = outcome[0].fractional;
      eachWayOdds = outcome[0].eachWayOdds || '1';
      placeOdds = outcome[0].placeOdds || 1;
    }
    offerStatus = offer[0].status;
  }
  return { offerName, odds, oddsString, fractionalOdds, outcomeId, outcomeName, offerStatus, eachWayOdds, placeOdds };
};

export const getOutcome = (bet: PendingBet): Outcome | null => {
  const offer =
    bet.event.isRacing && bet.event.raceOffers
      ? bet.event.raceOffers
      : bet.event.details && bet.event.details.offers.filter(offerItem => offerItem.id === bet.offerId);

  if (offer && offer.length === 1) {
    const outcome = offer[0].outcomes.find(outcomeItem => outcomeItem.outcomeId === bet.outcomeId);

    return outcome || null;
  }
  return null;
};

export const getAccaEligibleBets = (pendingBets: PendingBet[]): PendingBet[] =>
  pendingBets.filter(pendingBet => pendingBet.selectedInMultiples);

export const removePlacedAccumulatorBet = (pendingBets: PendingBet[]) => {
  const lastPendingBetIndex = pendingBets.length - 1;
  for (let index = 0; index < lastPendingBetIndex; index += 1) {
    removePendingBet(pendingBets[index]);
  }
};

export const subscribeToBetSlipEvents = async (eventsToSubscribe: SubscribeEventInfo[]) => {
  const nonHrEventsToSubscribe = eventsToSubscribe.filter(event => event.sportId !== sportCategories.HORSES);
  await Promise.all(nonHrEventsToSubscribe.map(event => getEvent(event.sportId, event.id))).catch(() => {
    // do nothing
  });
};

export const getPendingBetsCounterInformation = (pendingBets: PendingBet[]): PendingBetsCounterData => {
  const eventIds = pendingBets.map(bet => bet.event.id);
  return {
    numberOfPendingBets: eventIds.length,
    numberOfUniqueEventBets: new Set(eventIds).size,
  } as PendingBetsCounterData;
};

export const getUniqueEventsFromPendingBetsToSubscribe = (pendingBets: PendingBet[]) => {
  const eventsToSubscribe: SubscribeEventInfo[] = [];

  pendingBets
    .map(({ event }) => ({ id: event.id, sportId: event.sportId }))
    .forEach(eventInfo => {
      if (!isEventInSubscriptionQueue(eventsToSubscribe, eventInfo)) {
        eventsToSubscribe.push(eventInfo);
      }
    });

  return eventsToSubscribe;
};

export const getPendingBetCreateTicketPayload = (bet: PendingBet) => {
  const { odds, oddsString, fractionalOdds } = getOfferState(bet);

  return {
    odds,
    fractionalOdds,
    eventId: bet.event.id,
    sportId: bet.event.sportId,
    offerId: bet.offerId,
    outcomeId: bet.outcomeId,
    oddsStr: oddsString,
    extraValue: bet.extraValue,
  };
};

export const addTrendingBetToPendingBets = async (
  eventId: number,
  sportId: number,
  offerId: number,
  outcomeId: number,
  remove: boolean | undefined,
  intl: IntlShape,
  locale: AvailableLanguage | undefined,
  callback: (() => void) | undefined,
) => {
  const event = await getEvent(sportId, eventId);
  if (!event) {
    return;
  }

  if (!remove) {
    const added = addPendingBet({ event, offerId, outcomeId }, intl, false);
    if (added) {
      trackSportradarAddToBeslip(event, locale);

      if (callback) {
        callback();
      }
    }
  } else {
    removePendingBet({ event, offerId, outcomeId });
  }
};

export const getBetCombinations = (accaBets: PendingBet[]) => {
  const betCombinations = combinations(accaBets);

  const betCombinationMapping: BetCombinations = {};
  betCombinations.forEach(combination => {
    const combinationLength = combination.length;
    const combinationMapping = betCombinationMapping[combinationLength];
    if (combinationMapping) {
      combinationMapping.push(combination);
    } else {
      betCombinationMapping[combinationLength] = [combination];
    }
  });

  return betCombinationMapping;
};

export const getEwOdds = (
  systemBet: SystemBetStakeDetails,
  bets: PendingBet[],
  systemBetStakesDetails: SystemBetStakeDetails[],
  availableSystemBetTypes: SystemBetTypeExtended[],
  original?: boolean,
) => {
  const isEW = getSystemBetsGlobalAllEWStatus(availableSystemBetTypes, systemBetStakesDetails) || systemBet.isEachWay;

  const ewOddsArr = getEWOddsForEachBetForAccumulatorBet(bets, isEW, original);
  return ewOddsArr.reduce((acc, odd) => [odd[0] * acc[0], odd[1] * acc[1]]);
};

export const calculateEWPayout = (
  systemBetStakeDetail: SystemBetStakeDetails,
  bets: PendingBet[],
  stake: number,
  systemBetStakesDetails: SystemBetStakeDetails[],
  availableSystemBetTypes: SystemBetTypeExtended[],
  original?: boolean,
) => {
  const ewOdds = getEwOdds(systemBetStakeDetail, bets, systemBetStakesDetails, availableSystemBetTypes, original);
  const firstPartEWPayout = ewOdds[0] * stake;
  const secondPartEWPayout = ewOdds[1] * stake;
  const firstPartEWPayoutWithPossibleTaxes = getFinalPayout({
    stake,
    odds: ewOdds[0],
    payout: firstPartEWPayout,
  });
  const secondPartEWPayoutWithPossibleTaxes = getFinalPayout({
    stake,
    odds: ewOdds[1],
    payout: secondPartEWPayout,
  });

  return firstPartEWPayoutWithPossibleTaxes + secondPartEWPayoutWithPossibleTaxes;
};

export const handlePlaceNextSystemBet = () => {
  const {
    bets: { systemBetsPlacingOrder },
  } = store.getState();

  const hasElements = systemBetsPlacingOrder.length > 0;

  if (hasElements) {
    const systemBet = systemBetsPlacingOrder[0];
    removeSystemBetStakeDetails(systemBet.systemBetId);
    removeSystemBetStakeValidationStatus(String(systemBet.systemBetId));

    const copyOfSystemBetsPlacingOrder = cloneDeep(systemBetsPlacingOrder).slice(1);
    setSystemBetPlacingOrder(copyOfSystemBetsPlacingOrder);
  }
};

export const getBoostedSystemBets = (tickets: Ticket[]) => {
  const envelopesList: TicketSystemBetDetailsInfoExtended[] = [];

  tickets.forEach(ticket => {
    if (ticket.systemBetDetails && ticket.systemBetDetails.length) {
      ticket.systemBetDetails.forEach(systemBetDetails => {
        systemBetDetails.tickets.forEach(systemBetDetailTicket => {
          if (systemBetDetailTicket.multiplier) {
            envelopesList.push({ ...systemBetDetailTicket, header: systemBetDetails.header });
          }
        });
      });
    } else if (ticket.systemBetTypeId === null && !ticket.systemBetDetails && ticket.accumulator !== null) {
      envelopesList.push({
        id: accaSystemBet.id,
        header: SYSTEM_BET_TYPE.ACCA,
        selectionIds: '',
        stake: ticket.amount,
        odds: ticket.winOdds,
        boostedOdds: ticket.balancedWinOdds || 0,
        fractionalOdds: ticket.winOddsFractional,
        boostedFractionalOdds: ticket.balancedWinOddsFractional || '',
        payout: ticket.netPayout || ticket.payout,
        multiplier: ticket.multiplier,
        result: null,
      });
    }
  });

  return envelopesList.sort((a, b) => {
    if (a.multiplier !== null && b.multiplier !== null) {
      if (a.multiplier < b.multiplier) {
        return 1;
      }

      return -1;
    }

    return 0;
  });
};

export const checkIfAreOutcomeChanges = (outcomeSelectionByEventChanged: OutcomeSelectionByEventChanged) =>
  Object.keys(outcomeSelectionByEventChanged)
    .map(key => outcomeSelectionByEventChanged[key])
    .reduce((acc, flag) => acc || flag, false);
